// src/data/unleashedProductMap.ts
// ------------------------------
// Repository for `unleashed_product_map` (db-66) — the Order Builder mapping of a
// fixed-enum orderable item kind (device/fee/shoe) to an Unleashed product code.
// Backs the Manage → Unleashed → Product Mapping admin screen. Typed via
// getTypedClient().

import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

export type ProductMapRow = Database["public"]["Tables"]["unleashed_product_map"]["Row"];
type MapInsert = Database["public"]["Tables"]["unleashed_product_map"]["Insert"];
type WriteResult = { error: { message: string } | null };

// The whole map (14 fixed rows — tiny). null on error / no client.
export async function listProductMap(): Promise<ProductMapRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("unleashed_product_map")
    .select("*")
    .order("item_kind")
    .order("item_key")
    .limit(1000);
  if (error) {
    logger.warn("listProductMap failed:", error.message);
    return null;
  }
  return (data as unknown as ProductMapRow[]) ?? [];
}

// Set (upsert) the product code for a given (item_kind, item_key, year_bucket).
// year_bucket defaults to "" (the base row); stationery passes a year bucket
// (db-115) to map a year-specific STA code. Relies on the unique
// (item_kind, item_key, year_bucket) constraint so a repeat write updates in place.
// Stamps updated_at. Logs on error and returns { error }.
export async function setMapping(
  itemKind: string,
  itemKey: string,
  productCode: string,
  yearBucket = ""
): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const row = {
    item_kind: itemKind,
    item_key: itemKey,
    year_bucket: yearBucket,
    product_code: productCode,
    updated_at: new Date().toISOString(),
  };
  const { error } = await c
    .from("unleashed_product_map")
    .upsert(row as MapInsert, { onConflict: "item_kind,item_key,year_bucket" });
  if (error) logger.warn("setMapping failed:", error.message);
  return { error };
}
